import { defineStore } from 'pinia';
import apiClient from '@/util/apiClient';
import { shouldThrottle } from '@/util/rateLimit.js';
import { useComplexMapStore } from '@/store/complexMapStore';

export interface SimpleLocationDTO {
	locationId: number;
	name?: string | null;
	addressLine1?: string | null;
	addressLine2?: string | null;
	city?: string | null;
	state?: string | null;
	postalCode5?: string | null;
	latitude?: number | null;
	longitude?: number | null;
	[key: string]: unknown;
}

export interface LocationDTO extends SimpleLocationDTO {
	country?: string | null;
	navigationNote?: string | null;
	verified?: boolean | null;
}

export interface SpringPage<T> {
	content: T[];
	totalElements: number;
	totalPages: number;
	number: number;
	size: number;
	[key: string]: unknown;
}

export interface StandardizeAddressInput {
	street1: string;
	street2?: string;
	city: string;
	state: string;
	zip5?: string;
	zip4?: string;
}

export interface StandardizeAddressResponse {
	status?: string;
	message?: string;
	original?: unknown;
	standardized?: unknown;
	confirmationToken?: string;
}

export interface LocationResolveResponse {
	outcome?: string;
	location?: LocationDTO;
	nearbyCandidates?: LocationDTO[];
}

interface LocationState {
	searchResults: SimpleLocationDTO[];
	loading: boolean;
	_idCache: Map<number, LocationDTO>;
}

function isFullLocationDTO(location: LocationDTO | null | undefined): location is LocationDTO {
	return Boolean(location && Object.prototype.hasOwnProperty.call(location, 'country'));
}

export const useLocationStore = defineStore('locationStore', {
	state: (): LocationState => ({
		searchResults: [],
		loading: false,
		_idCache: new Map<number, LocationDTO>()
	}),

	actions: {
		async fetchPaged({
			page = 0,
			size = 20,
			query = '',
			sortField = 'quality',
			sortDir = 'asc',
			unverifiedOnly = false,
			incompleteOnly = false,
			highRiskOnly = false
		}: {
			page?: number;
			size?: number;
			query?: string;
			sortField?: string;
			sortDir?: string;
			unverifiedOnly?: boolean;
			incompleteOnly?: boolean;
			highRiskOnly?: boolean;
		} = {}): Promise<SpringPage<SimpleLocationDTO>> {
			try {
				const params: Record<string, unknown> = { page, size, sortField, sortDir };

				if (query.trim()) params['q'] = query.trim();
				if (unverifiedOnly) params['unverifiedOnly'] = true;
				if (incompleteOnly) params['incompleteOnly'] = true;
				if (highRiskOnly) params['highRiskOnly'] = true;

				const { data } = await apiClient.get<SpringPage<SimpleLocationDTO>>('/locations/page', { params });
				return data;
			} catch {
				return { content: [], totalElements: 0, totalPages: 0, number: 0, size };
			}
		},

		async search(query: string, limit = 15): Promise<SimpleLocationDTO[]> {
			if (shouldThrottle('locationSearch', 100)) return this.searchResults;

			const q = query.trim();
			if (!q || q.length < 2) {
				this.searchResults = [];
				return [];
			}

			try {
				const { data } = await apiClient.get<SimpleLocationDTO[]>('/locations/search', {
					params: { q, limit }
				});
				this.searchResults = Array.isArray(data) ? data : [];
				return this.searchResults;
			} catch {
				this.searchResults = [];
				return [];
			}
		},

		async searchByName(name: string): Promise<LocationDTO[]> {
			if (shouldThrottle('locationSearchByName', 100)) return [];

			const n = name.trim();
			if (!n) return [];

			try {
				const { data } = await apiClient.get<LocationDTO[]>('/locations', {
					params: { name: n }
				});
				return Array.isArray(data) ? data : [];
			} catch {
				return [];
			}
		},

		async fetchById(locationId: number, { force = false }: { force?: boolean } = {}): Promise<LocationDTO | null> {
			const cached = this._idCache.get(locationId);
			if (!force && isFullLocationDTO(cached)) return cached;

			try {
				const { data } = await apiClient.get<LocationDTO>(`/locations/${String(locationId)}`);
				this._idCache.set(locationId, data);
				return data;
			} catch {
				return null;
			}
		},

		async findNearby(lat: number, lng: number, radius = 1000): Promise<LocationDTO[]> {
			try {
				const { data } = await apiClient.get<LocationDTO[]>('/locations/nearby', {
					params: { lat, lng, radius }
				});
				return Array.isArray(data) ? data : [];
			} catch {
				return [];
			}
		},

		async standardizeAddress(addressInput: StandardizeAddressInput): Promise<StandardizeAddressResponse> {
			const { data } = await apiClient.post<StandardizeAddressResponse>('/addresses/standardize', addressInput);
			return data;
		},

		async resolveLocation(addressConfirmationToken: string, name: string | null = null, shortName: string | null = null, { forceCreate = false }: { forceCreate?: boolean } = {}): Promise<LocationResolveResponse> {
			const payload: Record<string, unknown> = { addressConfirmationToken, name, shortName };
			if (forceCreate) payload['forceCreate'] = true;

			const { data } = await apiClient.post<LocationResolveResponse>('/locations/resolve', payload);
			if (data.location) {
				this._idCache.set(data.location.locationId, data.location);
			}
			return data;
		},

		async createManualLocation(payload: Record<string, unknown>): Promise<LocationResolveResponse> {
			const { data } = await apiClient.post<LocationResolveResponse>('/locations/save/manual', payload);
			if (data.location) {
				this._idCache.set(data.location.locationId, data.location);
			}
			return data;
		},

		async updateLocation(locationId: number, data: Record<string, unknown>): Promise<LocationDTO> {
			const { data: updated } = await apiClient.patch<LocationDTO>(`/locations/${String(locationId)}`, data);
			this._idCache.set(locationId, updated);
			useComplexMapStore().invalidate(locationId);
			return updated;
		},

		async updateLocationCoordinates(locationId: number, data: { latitude: number | null; longitude: number | null; navigationNote?: string; verify?: boolean }): Promise<LocationDTO> {
			const payload: Record<string, unknown> = {
				latitude: data.latitude,
				longitude: data.longitude
			};

			if (Object.prototype.hasOwnProperty.call(data, 'navigationNote')) {
				payload['navigationNote'] = data.navigationNote;
			}
			if (Object.prototype.hasOwnProperty.call(data, 'verify')) {
				payload['verify'] = data.verify;
			}

			const { data: updated } = await apiClient.patch<LocationDTO>(`/locations/edit/${String(locationId)}/coordinates`, payload);
			this._idCache.set(locationId, updated);
			useComplexMapStore().invalidate(locationId);
			return updated;
		},

		async deleteLocation(locationId: number): Promise<void> {
			await apiClient.delete(`/locations/delete/${String(locationId)}`);
			this._idCache.delete(locationId);
			useComplexMapStore().invalidate(locationId);
		},

		async verifyLocation(locationId: number): Promise<LocationDTO> {
			const { data } = await apiClient.post<LocationDTO>(`/locations/save/${String(locationId)}/verify`);
			this._idCache.set(locationId, data);
			useComplexMapStore().invalidate(locationId);
			return data;
		},

		async unverifyLocation(locationId: number): Promise<LocationDTO> {
			const { data } = await apiClient.delete<LocationDTO>(`/locations/delete/${String(locationId)}/verify`);
			this._idCache.set(locationId, data);
			useComplexMapStore().invalidate(locationId);
			return data;
		},

		getCachedById(locationId: number): LocationDTO | null {
			return this._idCache.get(locationId) ?? null;
		}
	}
});
